import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  signIn, 
  clearError
} from '../redux/auth/authSlice';

import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import Alert from "../components/shared/Alert/Alert";
import { TextField } from "../components/shared/TextField/TextField";
import Preloader from "../components/Preloader";

import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  ArrowRight,
  Shield,
  Users,
  TrendingUp,
  Heart
} from 'lucide-react';

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthState {
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

// Animation variants for better performance
const fadeInLeft = {
  initial: { opacity: 0, x: -50 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.8 }
};

const fadeInRight = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.8, delay: 0.2 }
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay: 0.8 }
};

const staggerChildren = {
  initial: { opacity: 0, x: -30 },
  animate: { opacity: 1, x: 0 }
};

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  
  const { 
    register, 
    handleSubmit, 
    formState: { errors, isValid, isDirty } 
  } = useForm<LoginCredentials>({
    mode: 'onChange',
    defaultValues: {
      email: "",
      password: "",
    },
  });
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Optimized selector with proper typing
  const { loading, error, isAuthenticated } = useSelector(
    (state: { auth: AuthState }) => state.auth
  );
  
  // Memoized features data
  const features = useMemo(() => [
    {
      icon: Shield,
      title: "Secure Practice Management",
      description: "Fully compliant platform with end-to-end encryption"
    },
    {
      icon: Users,
      title: "Client Connection Hub", 
      description: "Get found, advertise and more"
    },
    {
      icon: TrendingUp,
      title: "Practice Growth Tools",
      description: "Analytics, Get featured on our blog, and integrate with your EHR"
    }
  ], []);

  // Memoized callbacks
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const onSubmit = useCallback((data: LoginCredentials) => {
    dispatch(signIn({ username: data.email, password: data.password }));
  }, [dispatch]);

  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Effects
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/practice');
    }
  }, [isAuthenticated, navigate]);

  // Early return for loading state
  if (loading && !isDirty) {
    return <Preloader />;
  }
  
  return (
    <div className="min-h-screen bg-white">
      <div className="flex min-h-screen">
        {/* Left Side - Hero Section */}
        <div className="flex-1 bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 text-white p-12 flex flex-col justify-center relative overflow-hidden">
          {/* Optimized background decoration */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-20 left-20 w-32 h-32 bg-white rounded-full blur-xl" />
            <div className="absolute bottom-32 right-16 w-48 h-48 bg-purple-400 rounded-full blur-2xl" />
          </div>
          
          <motion.div
            {...fadeInLeft}
            className="relative z-10 max-w-xl mx-auto"
          >
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Welcome to
              <br />
              Therapist Direct
            </h1>
            
            <p className="text-xl mb-12 text-purple-100 leading-relaxed max-w-lg">
              Transform your practice with our comprehensive platform
              designed specifically for mental health professionals.
            </p>

            {/* Optimized Features List */}
            <div className="space-y-8 mb-12">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.div
                    key={feature.title}
                    {...staggerChildren}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
                    className="flex items-start space-x-4 group"
                  >
                    <div className="bg-white bg-opacity-20 p-3 rounded-lg group-hover:bg-opacity-30 transition-all duration-200">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-purple-100 leading-relaxed">{feature.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div {...fadeInUp} className="mt-12">
              <div 
              className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                {/* Add space between the text and the link */}
                <p className="text-white mb-2 font-semibold">New to our platform? &nbsp;
                <Link 
                  to="/register" 
                  className="font-bold text-yellow-300 underline hover:text-yellow-200 transition-colors duration-200"
                  >
                  Create your account today!
                </Link>
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-96 bg-gray-50 p-8 flex flex-col justify-center">
          <motion.div
            {...fadeInRight}
            className="max-w-sm mx-auto w-full"
          >
            {/* Welcome Back Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-8 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome Back</h2>
              <p className="text-gray-600 text-sm">Sign in to continue to your practice dashboard</p>
            </div>

            {/* Optimized Error Alert */}
            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert
                    hasCloseButton
                    message={error}
                    variant="error"
                    className="mb-6"
                    dismissAlert={handleClearError}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Login Form */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" 
                    aria-hidden="true"
                  />
                  <TextField
                    {...register("email", { 
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Please enter a valid email address"
                      }
                    })}
                    label=""
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="Enter your email address"
                    className={`pl-12 pr-4 py-4 w-full border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 ${
                      errors.email
                        ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 hover:border-gray-300"
                    }`}
                    errorMessage={errors.email?.message}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" 
                    aria-hidden="true"
                  />
                  <TextField
                    {...register("password", { 
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters"
                      }
                    })}
                    label=""
                    type={showPassword ? "text" : "password"}
                    id="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className={`pl-12 pr-12 py-4 w-full border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 ${
                      errors.password
                        ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 hover:border-gray-300"
                    }`}
                    errorMessage={errors.password?.message}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 transition-colors" 
                    {...register("rememberMe")}
                  />
                  <span className="ml-2 text-sm text-gray-600">Remember me</span>
                </label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded"
                >
                  Forgot password?
                </Link>
              </div>

              <motion.button
                type="submit"
                disabled={loading || !isValid}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                className={`w-full flex items-center justify-center py-4 px-6 rounded-xl text-base font-semibold text-white transition-all duration-200 ${
                  loading || !isValid
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-purple-600 hover:bg-purple-700 shadow-lg hover:shadow-xl"
                } focus:outline-none focus:ring-4 focus:ring-purple-500/20`}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center">
                    Sign in to Dashboard
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </div>
                )}
              </motion.button>

              <hr className="my-4 border-gray-200" />
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600">Need help getting started? 
                <button className   ="text-purple-600 hover:text-purple-700 font-medium">Contact our support team</button>
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;